import { heroHttpClient } from '@ll-platform/frontend/core/api/HeroHttpClient';
import {
  automapDates,
  type SerializedJson,
} from '@ll-platform/frontend/core/api/helpers/dateMapper';
import type {
  ActiveUserProfile,
  AuthCallbackResponseDto,
  GoogleOAuthCallbackParams,
  LocalAuthPayloadDto,
  RegisterDto,
  RequestResetPasswordDto,
  UpdatePasswordDto,
  UserTokens,
} from '@ll-platform/frontend/core/auth/types';
import {
  clearUserTokens,
  getUserTokens,
  saveUserTokens,
} from '@ll-platform/frontend/core/auth/utils/localStorage';
import { assertDefined } from '@ll-platform/frontend/utils/types/types';

class AuthenticationService {
  async localAuthSignIn(args: LocalAuthPayloadDto): Promise<UserTokens> {
    const tokens = await heroHttpClient.unwrappedHttpRequest<UserTokens>({
      config: {
        method: 'POST',
        url: '/v1/auth/login',
        data: args,
      },
      handleUnauthorized: false,
    });

    return tokens;
  }

  async getActiveUser(): Promise<ActiveUserProfile> {
    const user = await heroHttpClient.unwrappedHttpRequest<
      SerializedJson<ActiveUserProfile>
    >({
      config: {
        method: 'GET',
        url: '/v1/auth/profile',
      },
      handleUnauthorized: () => {
        clearUserTokens();
        throw new Error('Unauthorized. Please log in again');
      },
    });

    return automapDates<ActiveUserProfile>(user);
  }

  async register(args: RegisterDto): Promise<UserTokens> {
    const tokens = await heroHttpClient.unwrappedHttpRequest<UserTokens>({
      config: {
        method: 'POST',
        url: '/v1/auth/register',
        data: args,
      },
      handleUnauthorized: false,
    });

    return tokens;
  }

  async requestResetPassword(args: RequestResetPasswordDto): Promise<void> {
    await heroHttpClient.unwrappedHttpRequest<void>({
      config: {
        method: 'POST',
        url: '/v1/auth/request-reset-password',
        data: args,
      },
      handleUnauthorized: false,
    });
  }

  async updatePassword(args: UpdatePasswordDto): Promise<void> {
    await heroHttpClient.unwrappedHttpRequest<void>({
      config: {
        method: 'POST',
        url: '/v1/auth/update-password',
        data: args,
      },
      handleUnauthorized: false,
    });
  }

  async continueWithGoogle(
    args: GoogleOAuthCallbackParams,
  ): Promise<AuthCallbackResponseDto> {
    const response =
      await heroHttpClient.unwrappedHttpRequest<AuthCallbackResponseDto>({
        config: {
          method: 'GET',
          url: '/v1/auth/google/callback',
          params: args,
        },
        handleUnauthorized: false,
      });

    return response;
  }

  // Used to refresh the tokens after updating account information
  async reloadTokens() {
    const existingTokens = getUserTokens();
    assertDefined(existingTokens, 'existingTokens');
    const tokens = await this.makeReloadTokens(existingTokens);
    saveUserTokens(tokens);
  }

  private async makeReloadTokens(currentTokens: UserTokens) {
    return await heroHttpClient.unwrappedHttpRequest<UserTokens>({
      config: {
        method: 'POST',
        url: '/v1/auth/reload-token',
        data: currentTokens,
      },
      handleUnauthorized: false,
    });
  }
}

export const authenticationService = new AuthenticationService();
